import { CancelToken } from 'axios';
import { RequestsPerPage } from '../types';
import axios from '../../utils/axios';

const getContractPaymentsPerPage = async ({
  page, limit, searchValue, type, paidStatus, location, year,
}: RequestsPerPage & {
  type: 'fee' | 'tuition',
  paidStatus?: 'paid' | 'pending',
  location?: string,
  year?: number,
}, cancelToken?: CancelToken) => {
  let url = `/api/contract-payments?page=${page}&limit=${limit}&searchValue=${searchValue}&type=${type}`;

  if (paidStatus) {
    url += `&paidStatus=${paidStatus}`;
  }
  if (location) {
    url += `&location=${location}`;
  }

  if (year) {
    url += `&year=${year}`;
  }

  const response = await axios.get(url, { cancelToken });

  return response.data;
};

const getIndicators = async (type: 'fee' | 'tuition', year?: number) => {
  let url = `/api/contract-payments/indicators?type=${type}`;

  if (year) {
    url += `&year=${year}`;
  }

  const response = await axios.get(url);
  return response.data;
};

const getPaymentDocumentUrl = async (contractPaymentId: string) => {
  const response = await axios.get(`/api/contract-payments/${contractPaymentId}/document-url`);
  return response.data;
};

const postRegisterPayment = async ({
  contractId,
  amount,
  comment,
  type,
  file,
  paymentMethod,
}: {
  contractId: string;
  amount: number;
  comment: string;
  file: File;
  type: 'fee' | 'tuition';
  paymentMethod?: string;
}) => {
  const formData = new FormData();
  formData.append('contractId', contractId);
  formData.append('amount', amount.toString());
  formData.append('comment', comment);
  formData.append('type', type);
  formData.append('file', file);
  if (paymentMethod) {
    formData.append('paymentMethod', paymentMethod);
  }

  const response = await axios.post('/api/contract-payments/register', formData);

  return response.data;
};

const getTuitionsPerPage = async ({
  page, limit, searchValue, paidStatus, location, year,
}: RequestsPerPage & {
  paidStatus?: 'paid' | 'pending',
  location?: string,
  year?: number,
}, cancelToken?: CancelToken) => {
  const response = await axios.get(`/api/contract-payments/tuitions?page=${page}&limit=${limit}&searchValue=${searchValue}&paidStatus=${paidStatus}&location=${location}&year=${year}`, { cancelToken });

  return response.data;
};

const getFeesPerPage = async ({
  page, limit, searchValue, paidStatus, location, year,
}: RequestsPerPage & {
  paidStatus?: 'paid' | 'pending',
  location?: string,
  year?: number,
}, cancelToken?: CancelToken) => {
  const response = await axios.get(`/api/contract-payments/fees?page=${page}&limit=${limit}&searchValue=${searchValue}&paidStatus=${paidStatus}&location=${location}&year=${year}`, { cancelToken });
  return response.data;
};

const getAllPaymentDocumentsPerPage = async ({
  page, limit, searchValue, year,
}: RequestsPerPage, cancelToken?: CancelToken) => {
  let url = `/api/contract-payments/all-payment-documents?page=${page}&limit=${limit}&searchValue=${searchValue || ''}`;

  if (year) {
    url += `&year=${year}`;
  }

  const response = await axios.get(url, { cancelToken });
  return response.data;
};

const deletePaymentDocument = async (paymentDocumentId: string) => {
  const response = await axios.delete(`/api/contract-payments/payment-document/${paymentDocumentId}`);
  return response.data;
};

const updatePaymentDocument = async (
  paymentDocumentId: string,
  amount: number,
  comment: string,
  file?: File,
  paymentMethod?: string,
) => {
  const formData = new FormData();
  formData.append('amount', amount.toString());
  formData.append('comment', comment);
  if (paymentMethod) {
    formData.append('paymentMethod', paymentMethod);
  }
  if (file) {
    formData.append('file', file);
  }

  const response = await axios.put(`/api/contract-payments/payment-document/${paymentDocumentId}`, formData);
  return response.data;
};

const getAllPaymentDocumentsExcel = async (searchValue?: string, year?: number) => {
  let url = '/api/contract-payments/all-payment-documents/excel';
  const params: string[] = [];
  if (searchValue) {
    params.push(`searchValue=${encodeURIComponent(searchValue)}`);
  }
  if (year) {
    params.push(`year=${year}`);
  }
  if (params.length > 0) {
    url += `?${params.join('&')}`;
  }
  const response = await axios.get(url, { responseType: 'blob' });
  return response.data;
};

const validatePaymentDocument = async (paymentDocumentId: string, studentId: string) => {
  const response = await axios.put(`/api/contract-payments/payment-document/${paymentDocumentId}/validate`, { studentId });
  return response.data;
};

const getPaymentDocumentsByStudentId = async (studentId: string) => {
  const response = await axios.get(`/api/contract-payments/pending-tuition-payment/student/${studentId}`);
  return response.data;
};

export {
  getContractPaymentsPerPage,
  getIndicators,
  getPaymentDocumentUrl,
  postRegisterPayment,
  getTuitionsPerPage,
  getFeesPerPage,
  getAllPaymentDocumentsPerPage,
  getAllPaymentDocumentsExcel,
  deletePaymentDocument,
  updatePaymentDocument,
  validatePaymentDocument,
  getPaymentDocumentsByStudentId,
};
